import * as React from "react";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import {
  ChevronDown20Regular,
  ChevronRight20Regular,
  ArrowSync20Regular,
  Save20Regular,
  Money20Regular,
  Box20Regular,
  ArrowTrending20Regular,
  People20Regular,
  ReceiptMoney20Regular,
  ChartMultiple20Regular,
  Wallet20Regular,
  Vault20Regular,
  DocumentBulletList20Regular,
  Shield20Regular,
  // 16er-Varianten für die Legende: Fluent-Icons tragen feste `width`/`height`
  // im SVG — ein `style={{ fontSize: 14 }}` an einem 20er-Icon bleibt wirkungslos
  // und stellte ein 20px-Warndreieck neben 11px-Text.
  ArrowSync16Regular,
  Globe16Regular,
  Warning16Regular,
  LockClosed20Regular,
  LockOpen20Regular,
  Organization20Regular,
  Calculator20Regular,
} from "@fluentui/react-icons";
import { apiFetch } from "../utils/apiFetch";
import { useAuth } from "../context/AuthContext";
import { useStickyStackOffset } from "../hooks/useStickyStackOffset";
import { GroupedNumberInput } from "./common/GroupedNumberInput";
import { REFINED } from "./risikoanalyseStep/tokens";
import type {
  Decoded,
  FieldRowDef,
  FieldType,
  KPIYear,
  RowDef,
  SectionRowDef,
  YesNoNumText,
  YesNoText,
} from "./kennzahlen/kpiTypes";
import {
  decode,
  encode,
  fmtEur,
  fmtEurMitQuote,
  fmtNum,
  parseEur,
  parseEuroStrict,
} from "./kennzahlen/kpiFormat";
import {
  aggregateCell,
  buildConsolidatedDecoded,
  isFieldRow,
  resolveSectionAggRows,
  type AggCell,
  type FirmYearData,
} from "./kennzahlen/kpiAggregate";
import {
  buildCollapsibleIds,
  buildGroupAncestors,
  buildGroupFieldChildren,
  filterVisibleRows,
  groupCountLabels,
} from "./kennzahlen/rowCollapse";
import { accentOf } from "./kennzahlen/sectionAccents";
import {
  brancheFeldZeilen,
  brancheZeilenMergen,
  revBranchenLesen,
  REV_BRANCHEN_HEADER_ID,
  type BrancheZeile,
} from "./kennzahlen/revBranchen";
import { activeGruppen, useBrancheGruppen } from "../data/brancheGruppen";
import { branchengruppenLaden } from "../services/branchengruppeService";
import { SearchableSelect, type SelectOption } from "./forms/widgets/SearchableSelect";
import { ConsolidatedAmountCell } from "./kennzahlen/ConsolidatedAmountCell";
import { CalculatorPopover } from "./kennzahlen/CalculatorPopover";
import { buildColorMap, KZ_REST } from "./kennzahlen/scopeColors";
import { RING_SIZE } from "./kennzahlen/splitRing";
import { OrganigrammPopup } from "./redesign/OrganigrammPopup";
import { useVerbundScope } from "./verbundScope/useVerbundScope";
import { VerbundScopeBar } from "./verbundScope/VerbundScopeBar";
import { bestaetigen } from "./redesign/bestaetigen";

// Typen (Decoded/RowDef/…) und die Kodier-/Formathelfer liegen jetzt in
// components/kennzahlen/ — react-frei, damit Aggregation und Tests sie ohne DOM
// ziehen können. Die früheren lokalen Kopien sind ersatzlos entfallen.

/**
 * Euro-Wert einer Zelle als Zahl — gleichgültig, ob er als Zahl (Euro-Eingabe
 * dieses Reiters) oder als String (Erfassen-Wizard, `otherInfo`-Blob) abgelegt
 * wurde. Derselbe Key kann beides tragen, je nachdem, wo zuletzt erfasst wurde.
 *
 * Steht seit der Bruttolohn-Sektion VOR dem Katalog, weil deren Summary ihn
 * braucht. Keiner der beiden Einzelhelfer genügt: `fmtEur` druckt einen String
 * roh durch (`String.prototype.toLocaleString` ignoriert die Currency-Optionen →
 * „2000000" statt „2.000.000 €"), und `parseEur` wirft bei einer Zahl
 * (`raw.replace is not a function`) und weißt damit den ganzen Reiter.
 */
const toEuroNumber = (v: number | string | undefined | null): number | undefined => {
  if (v === undefined || v === null || v === "") return undefined;
  return typeof v === "number" ? v : parseEur(String(v));
};

// ---------- Section + field definitions -----------------------------------

const buildRows = (branchenZeilen: readonly FieldRowDef[] = []): RowDef[] => {
  const rows: RowDef[] = [];

  const setF = (key: keyof Decoded) => (y: Decoded, v: any) => ({ ...y, [key]: v });
  const getF = (key: keyof Decoded) => (y: Decoded) => (y as any)[key];

  // -------- Umsatz --------
  rows.push({
    kind: "section",
    id: "umsatz",
    label: "Umsatz gesamt",
    Icon: ArrowTrending20Regular,
    summary: (y) => fmtEur(y.revenueTotal),
    // Im Verbund zeigt die Sektionszeile die aggregierte Zelle dieser Feldzeile
    // — Summe + Aufteilungs-Ring statt des reinen Textes.
    summaryAggRowId: "revenueTotal",
  });
  rows.push({
    kind: "field",
    sectionId: "umsatz",
    id: "revenueTotal",
    label: "Umsatz gesamt",
    type: "euro",
    get: getF("revenueTotal"),
    set: setF("revenueTotal"),
  });
  rows.push({
    kind: "field",
    sectionId: "umsatz",
    id: "revFluctuation",
    label: "Starke Schwankungen?",
    type: "yesNoText",
    indent: 1,
    get: (y) => y.revFluctuation ?? {},
    set: (y, v) => ({ ...y, revFluctuation: v }),
  });
  rows.push({
    kind: "field",
    sectionId: "umsatz",
    id: "revSpecial",
    label: "Sondereffekte (Sondergeschäfte)?",
    type: "yesNoText",
    indent: 1,
    get: (y) => y.revSpecial ?? {},
    set: (y, v) => ({ ...y, revSpecial: v }),
  });
  rows.push({
    kind: "field",
    sectionId: "umsatz",
    id: "revInside",
    label: "Innenumsätze?",
    type: "yesNoPartner",
    indent: 1,
    get: (y) => y.revInside ?? {},
    set: (y, v) => ({ ...y, revInside: v }),
  });
  // Verteilung auf Länder
  rows.push({
    kind: "header", sectionId: "umsatz", id: "umsatz_laender_header",
    label: "Verteilung auf Länder",
  });
  rows.push({
    kind: "field", sectionId: "umsatz", id: "revRegionDeEur", groupId: "umsatz_laender_header",
    label: "a) Deutschland (€)", type: "euro", indent: 1,
    get: getF("revRegionDeEur"), set: setF("revRegionDeEur"),
  });
  rows.push({
    kind: "field", sectionId: "umsatz", id: "revRegionEuEur", groupId: "umsatz_laender_header",
    label: "b) Europa (€)", type: "euro", indent: 1,
    get: getF("revRegionEuEur"), set: setF("revRegionEuEur"),
  });
  rows.push({
    kind: "field", sectionId: "umsatz", id: "revRegionWorldEur", groupId: "umsatz_laender_header",
    label: "c) Weltweit (€)", type: "euro", indent: 1,
    get: getF("revRegionWorldEur"), set: setF("revRegionWorldEur"),
  });
  rows.push({
    kind: "field", sectionId: "umsatz", id: "revRegionUsaCaEur", groupId: "umsatz_laender_header",
    label: "davon USA/Kanada (€)", type: "euro", indent: 2,
    get: getF("revRegionUsaCaEur"), set: setF("revRegionUsaCaEur"),
  });

  // Verteilung auf Branchen
  rows.push({
    kind: "header", sectionId: "umsatz", id: "umsatz_branchen_header",
    label: "Verteilung auf Branchen",
  });
  // Eine Zeile je Branchengruppe (Branchen der Firma + gespeicherte + per
  // „+ Branche" hinzugefügte) — dynamisch, siehe `kennzahlen/revBranchen.ts`.
  // Die frühere feste Vierer-Liste (Automotive, Schifffahrt, Luftfahrt,
  // Schienenverkehr) wird beim Lesen auf die passenden Gruppen abgebildet.
  rows.push(...branchenZeilen);

  // -------- Wareneinsatz --------
  rows.push({
    kind: "section",
    id: "wareneinsatz",
    label: "Wareneinsatz",
    Icon: Box20Regular,
    // Betrag UND Quote: zugeklappt (der Startzustand des Reiters) ist das die
    // einzige Zeile der Sektion, und die Quote ist beim Wareneinsatz die
    // aussagekräftigere der beiden Zahlen. Steht nur die Quote in den Daten,
    // steht auch nur sie hier — statt eines „—", das „nichts erfasst" behauptet,
    // obwohl eine Zeile darunter 45 % zeigt.
    summary: (y) => fmtEurMitQuote(y.cogsEur, y.cogsPct),
    // Im VERBUND gewinnt weiterhin die aggregierte Zelle des Betrags: Quoten
    // mehrerer Firmen lassen sich nicht addieren (`defaultAgg("percent")` ist
    // deshalb "none"). Eine Sektionszeile „45 % · 38 % · 51 %" wäre keine
    // Kennzahl, sondern eine Aufzählung — die Aufteilung des Betrags über die
    // Firmen ist dort die richtige Auskunft.
    summaryAggRowId: "cogsEur",
  });
  rows.push({
    kind: "field",
    sectionId: "wareneinsatz",
    id: "cogsEur",
    // „gesamt" wie beim Umsatz darüber: die Zeile hieß wie ihr Sektionstitel,
    // und die Quote eine Zeile tiefer trug denselben Wortstamm. Welche der
    // beiden die Leitzahl ist, war am Label nicht zu erkennen.
    label: "Wareneinsatz gesamt (€)",
    type: "euro",
    get: getF("cogsEur"),
    set: setF("cogsEur"),
  });
  rows.push({
    kind: "field",
    sectionId: "wareneinsatz",
    id: "cogsPct",
    label: "Wareneinsatzquote (%)",
    type: "percent",
    indent: 1,
    get: getF("cogsPct"),
    set: setF("cogsPct"),
  });
  rows.push({
    kind: "field",
    sectionId: "wareneinsatz",
    id: "cogsFluctuation",
    label: "Schwankungen?",
    type: "yesNoNumText",
    indent: 1,
    get: (y) => y.cogsFluctuation ?? {},
    set: (y, v) => ({ ...y, cogsFluctuation: v }),
  });

  // -------- Rohertrag --------
  rows.push({
    kind: "section",
    id: "rohertrag",
    label: "Rohertrag",
    Icon: Money20Regular,
    summary: (y) => fmtEur(y.grossProfit),
    summaryAggRowId: "grossProfit",
  });
  rows.push({
    kind: "field",
    sectionId: "rohertrag",
    id: "grossProfit",
    label: "Rohertrag",
    type: "euro",
    get: getF("grossProfit"),
    set: setF("grossProfit"),
  });
  rows.push({
    kind: "field",
    sectionId: "rohertrag",
    id: "profitFluctuation",
    label: "Schwankungen?",
    type: "yesNoNumText",
    indent: 1,
    get: (y) => y.profitFluctuation ?? {},
    set: (y, v) => ({ ...y, profitFluctuation: v }),
  });

  // -------- Menschen im und fürs Unternehmen --------
  rows.push({
    kind: "section",
    id: "beschaeftigte",
    label: "Menschen im und fürs Unternehmen",
    Icon: People20Regular,
    summary: (y) => (y.ma_gesamt && y.ma_gesamt !== "" ? String(y.ma_gesamt) : "—"),
  });
  // Mitarbeiter / Vollzeit / Teilzeit (verschachtelt)
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "ma_gesamt",
    label: "Mitarbeiter gesamt", type: "number",
    get: getF("ma_gesamt"), set: setF("ma_gesamt"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "avgConcurrentPersons",
    // Unterpunkt von „Mitarbeiter gesamt", deshalb direkt dahinter und
    // eingerückt. Davor (bisher) und ohne Einzug las es sich als zweite
    // Leitkennzahl neben der Kopfzahl; hinter `ma_midijobber` — der anderen
    // Stelle, an die eine Gleichzeitigkeitszahl gepasst hätte — wäre es optisch
    // in die Teilzeit-Gruppe gerutscht. Die Zuordnung trägt allein `indent`:
    // Reihenfolge und Einzug sind Darstellung, der Key bleibt eine eigene
    // Top-Level-Spalte in `ContactKpi.data`, keine Migration.
    label: "Mitarbeiter gesamt zur gleichen Zeit", type: "number", indent: 1,
    get: getF("avgConcurrentPersons"), set: setF("avgConcurrentPersons"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "ma_vollzeit",
    label: "Vollzeitkräfte", type: "number", indent: 1,
    get: getF("ma_vollzeit"), set: setF("ma_vollzeit"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "ma_monatsstunden",
    label: "Monatsstunden Vollzeit", type: "number", indent: 2,
    // Bezugsgröße EINER Vollzeitstelle, keine Menge: drei Firmen mit je 160
    // Stunden ergeben nicht 480, und ein Ring mit „33 % der Monatsstunden je
    // Firma" wäre schlicht erfunden. Wie bei den Prozentzeilen also keine Summe.
    agg: "none",
    get: getF("ma_monatsstunden"), set: setF("ma_monatsstunden"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "ma_teilzeit",
    label: "Teilzeitkräfte", type: "number", indent: 1,
    get: getF("ma_teilzeit"), set: setF("ma_teilzeit"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "ma_minijobber",
    label: "davon Minijobber", type: "number", indent: 2,
    get: getF("ma_minijobber"), set: setF("ma_minijobber"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "ma_midijobber",
    label: "davon Midijobber", type: "number", indent: 2,
    get: getF("ma_midijobber"), set: setF("ma_midijobber"),
  });
  // Die Bruttolohn-Summe stand früher hier — sie ist jetzt eine eigene Sektion
  // („bruttolohn", weiter unten). Ids und Speicherort sind dabei unverändert
  // geblieben.

  // Leiharbeiter
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "leiharbeiter_anzahl",
    label: "Leiharbeiter", type: "number",
    get: getF("leiharbeiter_anzahl"), set: setF("leiharbeiter_anzahl"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "leiharbeiter_regelmaessig",
    label: "regelmäßig", type: "text", indent: 1,
    get: getF("leiharbeiter_regelmaessig"), set: setF("leiharbeiter_regelmaessig"),
  });
  rows.push({
    kind: "header", sectionId: "beschaeftigte", id: "leiharbeiter_bereiche_header",
    label: "Tätigkeitsbereiche", indent: 1,
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "leiharbeiter_bereich_buero",
    groupId: "leiharbeiter_bereiche_header",
    label: "Büro", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("leiharbeiter_bereich_buero"), set: setF("leiharbeiter_bereich_buero"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "leiharbeiter_bereich_produktion",
    groupId: "leiharbeiter_bereiche_header",
    label: "Produktion", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("leiharbeiter_bereich_produktion"), set: setF("leiharbeiter_bereich_produktion"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "leiharbeiter_bereich_planung",
    groupId: "leiharbeiter_bereiche_header",
    label: "Planung", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("leiharbeiter_bereich_planung"), set: setF("leiharbeiter_bereich_planung"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "leiharbeiter_bereich_fremdgrundstuecke",
    groupId: "leiharbeiter_bereiche_header",
    label: "Arbeiten auf fremden Grundstücken", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("leiharbeiter_bereich_fremdgrundstuecke"), set: setF("leiharbeiter_bereich_fremdgrundstuecke"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "leiharbeiter_bereich_montage",
    groupId: "leiharbeiter_bereiche_header",
    label: "Montage", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("leiharbeiter_bereich_montage"), set: setF("leiharbeiter_bereich_montage"),
  });

  // Subunternehmer
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "subunternehmer_anzahl",
    label: "Subunternehmer", type: "number",
    get: getF("subunternehmer_anzahl"), set: setF("subunternehmer_anzahl"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "subunternehmer_umsatzanteil",
    label: "Umsatzanteil (%)", type: "percent", indent: 1,
    get: getF("subunternehmer_umsatzanteil"), set: setF("subunternehmer_umsatzanteil"),
  });
  rows.push({
    kind: "header", sectionId: "beschaeftigte", id: "subunternehmer_bereiche_header",
    label: "Tätigkeitsbereiche", indent: 1,
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "subunternehmer_bereich_buero",
    groupId: "subunternehmer_bereiche_header",
    label: "Büro", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("subunternehmer_bereich_buero"), set: setF("subunternehmer_bereich_buero"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "subunternehmer_bereich_produktion",
    groupId: "subunternehmer_bereiche_header",
    label: "Produktion", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("subunternehmer_bereich_produktion"), set: setF("subunternehmer_bereich_produktion"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "subunternehmer_bereich_planung",
    groupId: "subunternehmer_bereiche_header",
    label: "Planung", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("subunternehmer_bereich_planung"), set: setF("subunternehmer_bereich_planung"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "subunternehmer_bereich_fremdgrundstuecke",
    groupId: "subunternehmer_bereiche_header",
    label: "Arbeiten auf fremden Grundstücken", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("subunternehmer_bereich_fremdgrundstuecke"), set: setF("subunternehmer_bereich_fremdgrundstuecke"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "subunternehmer_bereich_montage",
    groupId: "subunternehmer_bereiche_header",
    label: "Montage", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("subunternehmer_bereich_montage"), set: setF("subunternehmer_bereich_montage"),
  });

  // Freelancer (Anker = reine Überschrift, keine Anzahl wie bei
  // Leiharbeiter/Subunternehmer — Aufwandshöhe und Tätigkeitsbereiche
  // hängen als Sub-Rows darunter).
  rows.push({
    kind: "header", sectionId: "beschaeftigte", id: "freelancer_header",
    label: "Freelancer",
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "freelancer_aufwand",
    groupId: "freelancer_header",
    label: "Aufwandshöhe (€)", type: "euro", indent: 1,
    get: getF("freelancer_aufwand"), set: setF("freelancer_aufwand"),
  });
  // Zweistufig: diese Überschrift hängt selbst unter „Freelancer" — „Freelancer"
  // einklappen nimmt die Tätigkeitsbereiche deshalb mit, ohne dass die
  // Sichtbarkeitsregel eine Sonderbehandlung für Header bräuchte.
  rows.push({
    kind: "header", sectionId: "beschaeftigte", id: "freelancer_bereiche_header",
    groupId: "freelancer_header",
    label: "Tätigkeitsbereiche", indent: 1,
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "freelancer_bereich_buero",
    groupId: "freelancer_bereiche_header",
    label: "Büro", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("freelancer_bereich_buero"), set: setF("freelancer_bereich_buero"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "freelancer_bereich_produktion",
    groupId: "freelancer_bereiche_header",
    label: "Produktion", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("freelancer_bereich_produktion"), set: setF("freelancer_bereich_produktion"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "freelancer_bereich_planung",
    groupId: "freelancer_bereiche_header",
    label: "Planung", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("freelancer_bereich_planung"), set: setF("freelancer_bereich_planung"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "freelancer_bereich_fremdgrundstuecke",
    groupId: "freelancer_bereiche_header",
    label: "Arbeiten auf fremden Grundstücken", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("freelancer_bereich_fremdgrundstuecke"), set: setF("freelancer_bereich_fremdgrundstuecke"),
  });
  rows.push({
    kind: "field", sectionId: "beschaeftigte", id: "freelancer_bereich_montage",
    groupId: "freelancer_bereiche_header",
    label: "Montage", type: "percent", indent: 2, hideWhenEmpty: true,
    get: getF("freelancer_bereich_montage"), set: setF("freelancer_bereich_montage"),
  });

  // -------- Bruttojahreslohn- und Gehaltssumme (BG-Meldung) --------
  //
  // Eigener Hauptpunkt statt Unterblock von „Menschen im und fürs Unternehmen":
  // dort lagen fünf Euro-Zeilen mitten zwischen Kopfzahlen, und die BG-Meldung
  // ist die Bemessungsgrundlage der Berufsgenossenschaftsbeiträge — ein
  // Aufwandsposten, keine Eigenschaft der Belegschaft.
  //
  // Position hinter „Menschen im und fürs Unternehmen" und vor der
  // Umsatzrendite: die Sektionsreihenfolge erzählt die GuV von oben nach unten
  // (Umsatz → Wareneinsatz → Rohertrag → Personal → Rendite). Hinter der
  // Rendite eingeordnet stünde ein Aufwand nach seinem eigenen Ergebnis.
  //
  // Die Zeilen-Ids wandern UNVERÄNDERT mit: `sectionId`/`indent` sind reine
  // Darstellung, die `bg_*` bleiben Keys im `otherInfo`-Blob. Deshalb keine
  // Migration und kein Datenverlust für bereits erfasste Jahre.
  rows.push({
    kind: "section",
    id: "bruttolohn",
    label: "Bruttojahreslohn- und Gehaltssumme (BG-Meldung)",
    Icon: ReceiptMoney20Regular,
    summary: (y) => fmtEur(toEuroNumber(y.bg_gesamt)),
    // Damit bekommt die Sektionszeile im Verbund Summe UND Aufteilungs-Ring wie
    // Umsatz/Wareneinsatz/Rohertrag. Ohne den Verweis bliebe hier ein
    // Summary-Text aus `buildConsolidatedDecoded` stehen — dieselbe Zahl, aber
    // ohne den Hinweis, aus welchen Firmen sie sich zusammensetzt.
    //
    // Der Verweis (statt einer eigenen Zahl-Funktion) ist hier besonders
    // wichtig: `bg_gesamt` ist die erste Leitkennzahl, die als FREITEXT im
    // `otherInfo`-Blob liegt — der Erfassen-Wizard schreibt den Rohstring. Über
    // `toEuroNumber` vorgeparst käme aus „ca. 2 Mio" die Zahl 2 heraus, und die
    // Sektionszeile meldete eine exakt aussehende Summe ohne „≥"-Marker.
    summaryAggRowId: "bg_gesamt",
  });
  rows.push({
    kind: "field", sectionId: "bruttolohn", id: "bg_gesamt",
    // Bleibt eine `field`-Zeile, obwohl der Sektionstitel dasselbe benennt:
    // Sektionszeilen rendern keinen `FieldEditor`, in die Überschrift „aufgelöst"
    // wäre die Summe nicht mehr eingebbar. Das Label kürzt stattdessen auf
    // „Gesamt (€)", damit der Sektionstitel eine Zeile darüber nicht doppelt.
    label: "Gesamt (€)", type: "euro",
    get: getF("bg_gesamt"), set: setF("bg_gesamt"),
  });
  rows.push({
    kind: "field", sectionId: "bruttolohn", id: "bg_buero",
    label: "Büromitarbeiter / kaufm. Angestellte (€)", type: "euro", indent: 1,
    get: getF("bg_buero"), set: setF("bg_buero"),
  });
  rows.push({
    kind: "field", sectionId: "bruttolohn", id: "bg_aussendienst",
    label: "Außendienst / Montage / Baustelle (€)", type: "euro", indent: 1,
    get: getF("bg_aussendienst"), set: setF("bg_aussendienst"),
  });
  rows.push({
    kind: "field", sectionId: "bruttolohn", id: "bg_aushilfen",
    label: "Aushilfen, Minijobber, Praktikanten (€)", type: "euro", indent: 1,
    get: getF("bg_aushilfen"), set: setF("bg_aushilfen"),
  });
  rows.push({
    kind: "field", sectionId: "bruttolohn", id: "bg_gf_inhaber",
    label: "Geschäftsführung / Inhaber (€)", type: "euro", indent: 1,
    get: getF("bg_gf_inhaber"), set: setF("bg_gf_inhaber"),
  });

  // -------- Umsatzrendite --------
  rows.push({
    kind: "section",
    id: "rendite",
    label: "Umsatzrendite",
    Icon: ChartMultiple20Regular,
    summary: (y) => (y.revenueMargin ? `${y.revenueMargin} %` : "—"),
  });
  rows.push({
    kind: "field",
    sectionId: "rendite",
    id: "revenueMargin",
    label: "Umsatzrendite (%)",
    type: "percent",
    get: getF("revenueMargin"),
    set: setF("revenueMargin"),
  });

  // -------- Eigenkapital --------
  rows.push({
    kind: "section",
    id: "eigenkapital",
    label: "Eigenkapital",
    Icon: Vault20Regular,
    summary: (y) =>
      y.equityEur || y.equityPct
        ? `${y.equityPct ? y.equityPct + "% / " : ""}${y.equityEur ?? "—"}`
        : "—",
  });
  rows.push({
    kind: "field",
    sectionId: "eigenkapital",
    id: "equityPct",
    label: "Eigenkapital-Quote (%)",
    type: "percent",
    get: getF("equityPct"),
    set: setF("equityPct"),
  });
  rows.push({
    kind: "field",
    sectionId: "eigenkapital",
    id: "equityEur",
    label: "Eigenkapital (€)",
    // Typ „text", weil hier historisch auch „ca. 2 Mio" steht — der Inhalt ist
    // aber Geld. Ohne das explizite agg/aggParse griffe defaultAgg("text") =
    // "none" und das Eigenkapital bliebe im Verbund unsummiert.
    type: "text",
    agg: "sum",
    aggParse: parseEuroStrict,
    get: getF("equityEur"),
    set: setF("equityEur"),
  });

  // -------- Liquidität --------
  rows.push({
    kind: "section",
    id: "liquiditaet",
    label: "Verfügbare Liquidität",
    Icon: Wallet20Regular,
    summary: (y) =>
      [y.liqShort, y.liqMid, y.liqLong].some((x) => x) ? "siehe Details" : "—",
  });
  rows.push({
    kind: "field",
    sectionId: "liquiditaet",
    id: "liqShort",
    label: "Kurzfristig",
    type: "text",
    indent: 1,
    get: getF("liqShort"),
    set: setF("liqShort"),
  });
  rows.push({
    kind: "field",
    sectionId: "liquiditaet",
    id: "liqMid",
    label: "Mittelfristig",
    type: "text",
    indent: 1,
    get: getF("liqMid"),
    set: setF("liqMid"),
  });
  rows.push({
    kind: "field",
    sectionId: "liquiditaet",
    id: "liqLong",
    label: "Langfristig",
    type: "text",
    indent: 1,
    get: getF("liqLong"),
    set: setF("liqLong"),
  });

  // -------- Verpflichtungen --------
  rows.push({
    kind: "section",
    id: "verpflichtungen",
    label: "Verpflichtungen",
    Icon: DocumentBulletList20Regular,
    summary: (y) =>
      [y.liabKK, y.liabLoan, y.liabOther].some((x) => x) ? "siehe Details" : "—",
  });
  rows.push({
    kind: "field",
    sectionId: "verpflichtungen",
    id: "liabKK",
    label: "Kontokorrent-Rahmen",
    type: "text",
    indent: 1,
    get: getF("liabKK"),
    set: setF("liabKK"),
  });
  rows.push({
    kind: "field",
    sectionId: "verpflichtungen",
    id: "liabLoan",
    label: "Kredite / Darlehen / Verbindlichkeiten",
    type: "text",
    indent: 1,
    get: getF("liabLoan"),
    set: setF("liabLoan"),
  });
  rows.push({
    kind: "field",
    sectionId: "verpflichtungen",
    id: "liabOther",
    label: "Sonstige Verpflichtungen",
    type: "text",
    indent: 1,
    get: getF("liabOther"),
    set: setF("liabOther"),
  });

  // -------- Bürgschaften --------
  rows.push({
    kind: "section",
    id: "buergschaften",
    label: "Bürgschaften",
    Icon: Shield20Regular,
    summary: (y) =>
      y.guaranteeTotal || y.guaranteeAvg ? "siehe Details" : "—",
  });
  rows.push({
    kind: "field",
    sectionId: "buergschaften",
    id: "guaranteeTotal",
    label: "Insgesamt",
    type: "text",
    indent: 1,
    get: getF("guaranteeTotal"),
    set: setF("guaranteeTotal"),
  });
  rows.push({
    kind: "field",
    sectionId: "buergschaften",
    id: "guaranteeAvg",
    label: "Im Schnitt",
    type: "text",
    indent: 1,
    get: getF("guaranteeAvg"),
    set: setF("guaranteeAvg"),
  });

  return rows;
};

// ---------- Styles --------------------------------------------------------

/**
 * Durchgehend REFINED-Tokens statt Fluent-`tokens.*`.
 *
 * Zwei Gründe, warum das kein Mischzustand sein darf: `docs/design-guideline.md`
 * verbietet REFINED und das ältere BRAND-System auf derselben Fläche — und der
 * Reiter hängt im Legacy-Renderpfad von `ContactDetails.tsx`, also OHNE
 * `.kvp-rd`-Wrapper. CSS-Variablen wie `var(--aub)` sind dort tot; es tragen
 * ausschließlich die JS-Token aus `risikoanalyseStep/tokens.ts`.
 */

/**
 * Zeilenfläche und Spaltentrenner. Bewusst blasser als `REFINED.aub50`: als
 * Dauerfläche über zehn Sektionen wäre der Wash zu laut, und ein Zebra-Muster
 * würde mit den farbcodierten Sektionsbalken um dieselbe Aufmerksamkeit
 * konkurrieren.
 */
const ROW_WASH = "#faf7fa";
const COL_LINE = "#f6f1f5";

/**
 * Spaltenbreiten. Die Label-Spalte trägt Sätze wie „Bruttojahreslohn- und
 * Gehaltssumme (BG-Meldung)" (Sektionstitel, dort zusätzlich hinter Chevron und
 * Icon) oder „Arbeiten auf fremden Grundstücken" auf Einzugsstufe 2 — mit
 * 15px-Werten reichten die früheren 320px nicht mehr. Die Jahresspalten müssen
 * Betrag UND Aufteilungs-Ring (22px plus Abstand) nebeneinander fassen.
 */
const LABEL_COL_W = 380;
const YEAR_COL_W = 190;

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    // KEINE Höhenbegrenzung: der Reiter wächst mit seinem Inhalt, gescrollt wird
    // die SEITE. Vorher füllte er per `useFillViewportHeight` exakt das Fenster
    // und die Tabelle scrollte in sich — bei 231px klebendem Kontaktkopf blieben
    // für die Kennzahlen ~287px, ein Viertel der Tabelle lag hinter einem
    // Innen-Scroller. Was beim Scrollen stehen bleibt, regelt jetzt der klebende
    // Stapel aus Werkzeugleiste und Tabellenkopf (siehe `useStickyStackOffset`).
    minHeight: 0,
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.md}px`,
    color: REFINED.ink,
    backgroundColor: REFINED.white,
  },
  topbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    ...shorthands.padding("8px", "12px"),
    ...shorthands.borderBottom("1px", "solid", REFINED.line),
    backgroundColor: REFINED.white,
    // Klebt unter dem Kontaktkopf. Muss sie auch: der „Speichern"-Knopf sitzt
    // hier und erscheint erst bei einer Änderung — scrollte die Leiste weg,
    // stünde er außer Sicht, sobald jemand weiter unten eine Zelle bearbeitet.
    // Der Offset wird gemessen, nicht geraten (Kopfhöhe schwankt je Kontakt, und
    // unter 768px klebt die Reiter-Leiste statt des Kopfes).
    position: "sticky",
    top: "var(--kt-head-top, 0px)",
    // Unter dem Kontaktkopf (z-index 50), über dem Tabellenkopf (3/4).
    zIndex: 6,
  },
  topbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  title: { fontWeight: 700, fontSize: `${REFINED.fs.lg}px`, color: REFINED.ink },
  yearControl: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding("3px", "7px"),
    backgroundColor: REFINED.white,
  },
  yearRange: {
    fontSize: `${REFINED.fs.xs}px`,
    fontWeight: 700,
    color: REFINED.ink,
    fontVariantNumeric: "tabular-nums",
  },
  yearStep: { ...shorthands.padding("2px", "7px") },
  topbarRight: { display: "flex", gap: "8px", alignItems: "center" },
  primaryBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    ...shorthands.padding("6px", "12px"),
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.aub,
    color: REFINED.white,
    cursor: "pointer",
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.sm}px`,
    fontWeight: 700,
    whiteSpace: "nowrap",
    boxShadow: REFINED.btnShadow,
    ":hover": {
      backgroundColor: REFINED.aub700,
      ...shorthands.borderColor(REFINED.aub700),
    },
  },
  ghostBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    ...shorthands.padding("6px", "10px"),
    ...shorthands.border("1.5px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    cursor: "pointer",
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.sm}px`,
    fontWeight: 700,
    whiteSpace: "nowrap",
    ":hover": {
      backgroundColor: REFINED.aub50,
      ...shorthands.borderColor(REFINED.aub200),
      color: REFINED.aub,
    },
  },
  ghostBtnOn: {
    backgroundColor: REFINED.aub50,
    ...shorthands.borderColor(REFINED.aub200),
    color: REFINED.aub,
  },
  ghostBtnOff: {
    opacity: 0.5,
    cursor: "not-allowed",
    ":hover": {
      backgroundColor: REFINED.white,
      ...shorthands.borderColor(REFINED.line),
      color: REFINED.ink,
    },
  },
  toast: {
    ...shorthands.padding("6px", "10px"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.okBg,
    color: REFINED.ok,
    fontSize: `${REFINED.fs.xxs}px`,
    fontWeight: 700,
  },
  errorBadge: {
    ...shorthands.padding("6px", "10px"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.errBg,
    color: REFINED.err,
    fontSize: `${REFINED.fs.xxs}px`,
    fontWeight: 700,
    maxWidth: "340px",
  },
  scopeWrap: { ...shorthands.padding("8px", "12px"), backgroundColor: REFINED.white },
  /**
   * Textfarbe `ink2`, nicht `ink3`: auf `fieldBg` kommt `ink3` nur auf 2,6:1 und
   * fiele damit unter die 4,5:1 der WCAG-AA-Schwelle. `ink3` ist laut
   * Design-Guideline für Platzhalter gedacht, nicht für Erklärtexte.
   */
  legend: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    ...shorthands.padding("6px", "12px"),
    fontSize: `${REFINED.fs.xxs}px`,
    color: REFINED.ink2,
    ...shorthands.borderBottom("1px", "solid", REFINED.line),
    backgroundColor: REFINED.fieldBg,
  },
  legendItem: { display: "inline-flex", alignItems: "center", gap: "4px" },
  legendLoading: {
    marginLeft: "auto",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    color: REFINED.aub,
    fontWeight: 700,
  },
  /**
   * Pflicht-Kennzeichnung „keine Konsolidierung" — deshalb NICHT im
   * Legenden-Grad (11px) und nicht in `goldInk`: der käme auf `goldBg` nur auf
   * 4,4:1 und verfehlte AA knapp. Das Gold trägt jetzt der Rahmen und das Icon
   * (als Grafik reichen 3:1), der Text steht in `ink`.
   */
  hinweis: {
    flexBasis: "100%",
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    marginTop: "2px",
    ...shorthands.padding("7px", "9px"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", REFINED.goldLine),
    backgroundColor: REFINED.goldBg,
    color: REFINED.ink,
    fontSize: `${REFINED.fs.sm}px`,
    fontWeight: 600,
    lineHeight: 1.45,
  },
  /**
   * KEIN Innen-Scroller mehr — bewusst auch nicht `overflow-x: auto`.
   *
   * Sobald hier eine Achse auf `auto` steht, wird dieses Element der Scrollport
   * für ALLE `position: sticky` darin: der Tabellenkopf klebte dann an einem
   * Kasten, der selbst nicht scrollt, bliebe also beim Scrollen der Seite NICHT
   * stehen. Waagerechter Überlauf ist deshalb Sache der Seite — `.dbody` trägt
   * unter 768px bereits `overflow-x: auto`, darüber fängt ihn `<main>`.
   */
  tableScroll: {
    backgroundColor: REFINED.white,
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: `${REFINED.fs.md}px`,
    tableLayout: "fixed",
  },
  thLabel: {
    position: "sticky",
    // Klebt unter Kontaktkopf UND eigener Werkzeugleiste (gemessen, s.
    // `useStickyStackOffset`). Mit `top: 0` schöbe sich der Tabellenkopf beim
    // Scrollen unter beide und die Jahresspalten wären nicht mehr zuzuordnen.
    top: "var(--kt-thead-top, 0px)",
    left: 0,
    zIndex: 4,
    backgroundColor: REFINED.fieldBg,
    ...shorthands.borderRight("1px", "solid", REFINED.line),
    ...shorthands.borderBottom("1px", "solid", REFINED.line),
    width: `${LABEL_COL_W}px`,
    minWidth: `${LABEL_COL_W}px`,
    textAlign: "left",
    ...shorthands.padding("8px", "10px"),
    fontSize: `${REFINED.fs.sm}px`,
    fontWeight: 700,
    color: REFINED.ink,
  },
  th: {
    position: "sticky",
    top: "var(--kt-thead-top, 0px)",
    zIndex: 3,
    backgroundColor: REFINED.fieldBg,
    ...shorthands.borderBottom("1px", "solid", REFINED.line),
    ...shorthands.padding("8px", "10px"),
    fontSize: `${REFINED.fs.sm}px`,
    fontWeight: 700,
    color: REFINED.ink,
    textAlign: "right",
    minWidth: `${YEAR_COL_W}px`,
    fontVariantNumeric: "tabular-nums",
  },
  thCurrent: {
    backgroundColor: REFINED.aub50,
    color: REFINED.aub,
    // Unterstrich als inset-Schatten statt border-bottom: die Trennlinie des
    // Kopfes soll bleiben, nicht ersetzt werden — und eine zweite Border
    // verschöbe die Zeilenhöhe der Kopfzeile.
    boxShadow: `inset 0 -2px 0 ${REFINED.aub}`,
  },
  /**
   * Abdeckungszeile. Sie ist die Absicherung dagegen, dass eine Summe aus 3 von
   * 5 Firmen für die Gesamtsumme gehalten wird — sie darf deshalb nicht der am
   * schlechtesten lesbare Text des Reiters sein: `ink2` (5,9:1) statt `ink3`
   * (2,7:1) und 11px statt 10px.
   */
  thCoverage: { fontSize: `${REFINED.fs.xxs}px`, fontWeight: 600, color: REFINED.ink2 },
  /** Spaltentrenner ab der zweiten Jahresspalte (die erste grenzt an die Label-Spalte). */
  colDivider: { ...shorthands.borderLeft("1px", "solid", COL_LINE) },
  sectionRow: {
    cursor: "pointer",
    // Hover auf der ganzen Zeile inklusive der sticky Label-Spalte: die trägt
    // eine eigene deckende Fläche und bliebe sonst als heller Block stehen.
    "&:hover td": { backgroundColor: REFINED.aub50 },
  },
  sectionLabelCell: {
    position: "sticky",
    left: 0,
    zIndex: 2,
    backgroundColor: ROW_WASH,
    ...shorthands.padding("9px", "12px"),
    ...shorthands.borderRight("1px", "solid", REFINED.line),
    ...shorthands.borderBottom("1px", "solid", REFINED.line),
    fontSize: `${REFINED.fs.md}px`,
    fontWeight: 700,
    color: REFINED.ink,
  },
  /**
   * Der Aufklapp-Griff von Sektions- UND Gruppenzeilen: ein echtes `<button>`
   * INNERHALB der Label-Zelle.
   *
   * Zwei Gründe für diese Form. Erstens Tastatur: die Zeile trägt zwar weiterhin
   * ein `onClick` (die ganze Breite bleibt Klickfläche), aber ein `<tr onClick>`
   * ist per Tastatur nicht erreichbar — ohne den Button ließe sich der Reiter
   * ausschließlich mit der Maus auf- und zuklappen. Zweitens Layout: `display:
   * flex` direkt auf dem `<td>` nähme der Zelle ihre table-cell-Natur, die
   * `<colgroup>`-Breite griffe je nach Browser nicht mehr und zusammen mit
   * `position: sticky` würde das Verhalten unzuverlässig.
   *
   * Alle Button-Defaults werden zurückgenommen (`font*: inherit` statt der
   * `font`-Kurzform — Griffel lehnt Shorthands ab), damit die Zeile aussieht wie
   * vorher.
   */
  rowToggleBtn: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    ...shorthands.padding("0"),
    ...shorthands.border("none"),
    backgroundColor: "transparent",
    color: "inherit",
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    letterSpacing: "inherit",
    textTransform: "inherit",
    textAlign: "left",
    cursor: "pointer",
  },
  sectionValueCell: {
    backgroundColor: ROW_WASH,
    // Vertikal 9px wie die Label-Zelle, horizontal aber 10px wie die Feldzellen:
    // die Summe einer Sektion und die Beträge darunter stehen in derselben
    // Zahlenkolonne und dürfen nicht um 2px gegeneinander versetzt sein.
    ...shorthands.padding("9px", "10px"),
    ...shorthands.borderBottom("1px", "solid", REFINED.line),
    textAlign: "right",
    fontSize: `${REFINED.fs.md}px`,
    fontWeight: 700,
    color: REFINED.ink,
    fontVariantNumeric: "tabular-nums",
  },
  /**
   * Der Summentext einer Sektion muss auch INNEN dasselbe Polster haben wie die
   * Werte darunter — das Zell-Padding allein reicht nicht: `readonlyValue` bringt
   * eigene 6px mit, ein nacktes `<span>` stand also 6px weiter rechts als die
   * Beträge derselben Spalte.
   */
  sectionValueText: { display: "inline-block", paddingRight: "6px" },
  /**
   * Im Verbund ersetzen `ConsolidatedAmountCell`-Zellen die Beträge; deren Text
   * endet um den Ring-Slot (plus Abstand) früher. Sektionen ohne Leitzeile
   * rendern weiterhin nur Text und brauchen dasselbe Maß, sonst liegen sie um
   * 28px daneben.
   */
  sectionValueTextRing: { paddingRight: `${RING_SIZE + 6}px` },
  fieldRow: {
    "&:hover td": { backgroundColor: ROW_WASH },
  },
  // `<th scope="row">` statt `<td>`: der Screenreader liest die Kennzahl sonst
  // nicht zur Zelle mit. Die th-Defaults (fett, zentriert) werden hier bewusst
  // zurückgenommen — optisch bleibt es eine Label-Zelle.
  fieldLabelCell: {
    position: "sticky",
    left: 0,
    zIndex: 1,
    backgroundColor: REFINED.white,
    ...shorthands.padding("6px", "10px"),
    ...shorthands.borderRight("1px", "solid", REFINED.line),
    ...shorthands.borderBottom("1px", "solid", COL_LINE),
    fontSize: `${REFINED.fs.sm}px`,
    fontWeight: 400,
    textAlign: "left",
    color: REFINED.ink,
  },
  fieldLabelIndent1: { paddingLeft: "44px" },
  fieldLabelIndent2: { paddingLeft: "76px" },
  /** Zwischenüberschrift („Tätigkeitsbereiche", „Verteilung auf Länder"). */
  fieldHeaderCell: {
    fontSize: `${REFINED.fs.xxs}px`,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: REFINED.aub,
  },
  /**
   * Gruppenzeile: wie eine Feldzeile, aber als Ganzes klickbar. Der Hover
   * spricht `th` MIT an — die Label-Spalte ist ein `<th scope="row">` mit
   * eigener deckender Fläche und bliebe sonst als weißer Block stehen, während
   * der Rest der Zeile einfärbt.
   */
  groupRow: {
    cursor: "pointer",
    // Zwei Einträge statt eines gruppierten „&:hover th, &:hover td": Griffel
    // zerlegt jede Regel in atomare Klassen, und Selektor-Gruppen kommen in
    // diesem Projekt nirgends vor — kein Grund, es hier als Erstes zu riskieren.
    "&:hover th": { backgroundColor: REFINED.aub50 },
    "&:hover td": { backgroundColor: REFINED.aub50 },
  },
  /**
   * „3 erfasst" in der eingeklappten Gruppenzeile. Bewusst im Legendengrad und
   * in `ink2`: die Zahl ist ein Hinweis auf Inhalt, kein Wert der Spalte — sie
   * darf nicht mit den Beträgen darüber und darunter um Aufmerksamkeit ringen.
   */
  brancheAktion: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    columnGap: "16px",
    rowGap: "6px",
    paddingTop: "4px",
    paddingBottom: "6px",
  },
  brancheAuswahl: { width: "320px", maxWidth: "100%" },
  brancheHinweis: {
    fontSize: `${REFINED.fs.xs}px`,
    color: REFINED.ink3,
    fontStyle: "italic",
    paddingBottom: "10px",
  },
  groupCount: {
    display: "inline-block",
    paddingRight: "6px",
    fontSize: `${REFINED.fs.xxs}px`,
    fontWeight: 600,
    color: REFINED.ink2,
    fontVariantNumeric: "tabular-nums",
  },
  fieldValueCell: {
    ...shorthands.padding("6px", "10px"),
    ...shorthands.borderBottom("1px", "solid", COL_LINE),
    verticalAlign: "top",
    textAlign: "right",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    ...shorthands.padding("4px", "6px"),
    // Rahmenbreite im Ruhezustand bereits 1.5px (transparent): sonst springt
    // die Zelle beim Fokussieren um einen halben Pixel.
    ...shorthands.border("1.5px", "solid", "transparent"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: "transparent",
    fontFamily: "inherit",
    fontSize: `${REFINED.fs.md}px`,
    fontWeight: 500,
    color: REFINED.ink,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    "::placeholder": { color: REFINED.ink3, fontWeight: 500 },
    ":hover": { ...shorthands.borderColor(REFINED.aub200) },
    ":focus": {
      outline: "none",
      ...shorthands.borderColor(REFINED.aub),
      backgroundColor: REFINED.white,
      boxShadow: REFINED.focusRing,
    },
  },
  textInput: {
    textAlign: "left",
    fontVariantNumeric: "normal",
  },
  readonlyValue: {
    ...shorthands.padding("1px", "6px"),
    fontSize: `${REFINED.fs.md}px`,
    fontWeight: 500,
    color: REFINED.ink,
    whiteSpace: "pre-wrap",
    minHeight: "20px",
    lineHeight: 1.35,
    fontVariantNumeric: "tabular-nums",
  },
  /**
   * „—" ist keine Zahl, sondern die Aussage „nicht erfasst" — zurückgenommen,
   * aber lesbar: `ink3` käme auf Weiß nur auf 2,9:1. Die Zurücknahme trägt jetzt
   * `ink2` gegenüber dem `ink` der Werte.
   */
  emptyValue: { color: REFINED.ink2, fontWeight: 500 },
  yesNoWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    alignItems: "flex-end",
  },
  yesNoToggleRow: {
    display: "inline-flex",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("8px"),
    overflow: "hidden",
    width: "fit-content",
    marginLeft: "auto",
    backgroundColor: REFINED.white,
  },
  yesNoBtn: {
    ...shorthands.padding("3px", "10px"),
    ...shorthands.border("none"),
    backgroundColor: "transparent",
    cursor: "pointer",
    fontFamily: REFINED.fontUI,
    fontSize: `${REFINED.fs.sm}px`,
    color: REFINED.ink,
    ":hover": { backgroundColor: REFINED.aub50, color: REFINED.aub },
  },
  yesNoBtnActiveYes: {
    backgroundColor: REFINED.okBg,
    color: REFINED.ok,
    fontWeight: 700,
    ":hover": { backgroundColor: REFINED.okBg, color: REFINED.ok },
  },
  yesNoBtnActiveNo: {
    backgroundColor: REFINED.errBg,
    color: REFINED.err,
    fontWeight: 700,
    ":hover": { backgroundColor: REFINED.errBg, color: REFINED.err },
  },
});

// ---------- Component ----------------------------------------------------

type Props = {
  contactId: number;
  contactName?: string;
};

/**
 * Der Katalog ohne die dynamischen Branchen-Zeilen. Sektionen und Gruppenköpfe
 * sind statisch — daraus kommen die klappbaren Ids für den Startzustand. Die
 * übrigen Ableitungen (Feldzeilen, Vorfahren, Leitzeilen) rechnet die Komponente
 * je Branchen-Stand neu, siehe `katalogAbleiten`.
 */
const STATIC_ROWS = buildRows();

/**
 * Sektionen = die section-Zeilen aus `buildRows()` selbst.
 *
 * Daneben stand früher ein zweites Array `sections` mit denselben
 * {id, label, Icon, summary} — eine rein konventionelle Doppelpflege ohne
 * technischen Grund. Die Render-Stelle holte die Sektion dort mit
 * `sections.find(...)!` heraus: eine in `buildRows()` ergänzte, im Array
 * vergessene Sektion hätte den kompletten Reiter mit einem TypeError geweißt —
 * und die Non-null-Assertion hätte TypeScript daran gehindert, davor zu warnen.
 */
const SECTIONS = STATIC_ROWS.filter((r): r is SectionRowDef => r.kind === "section");

// Die Klapp-Mechanik selbst liegt in `kennzahlen/rowCollapse.ts`.
const COLLAPSIBLE_IDS = buildCollapsibleIds(STATIC_ROWS);

/**
 * Katalog samt Ableitungen für einen Stand der Branchen-Zeilen. Ändert sich nur,
 * wenn sich die Branchen der Firma, die gespeicherten Gruppen oder die per
 * „+ Branche" hinzugefügten ändern — nicht bei jedem Tastendruck.
 */
const katalogAbleiten = (branchenZeilen: readonly FieldRowDef[]) => {
  const allRows = buildRows(branchenZeilen);
  /** Feldzeilen vorgefiltert — Basis jeder Verbund-Aggregation. */
  const fieldRows = allRows.filter(isFieldRow);
  const groupAncestors = buildGroupAncestors(allRows);
  return {
    allRows,
    fieldRows,
    groupAncestors,
    groupFieldChildren: buildGroupFieldChildren(allRows, groupAncestors),
    /** Leitzeile je Sektion — deren aggregierte Zelle die Sektionszeile im Verbund zeigt. */
    sectionAggRow: resolveSectionAggRows(SECTIONS, fieldRows),
  };
};

/**
 * „Nichts erfasst" — inklusive der leeren yesNo-Objekte: deren `get` liefert `{}`,
 * wenn nie etwas eingetragen wurde.
 */
const isEmptyValue = (v: unknown): boolean => {
  if (v === undefined || v === null || v === "") return true;
  if (typeof v === "object") {
    return Object.values(v as Record<string, unknown>).every(
      (x) => x === undefined || x === null || x === "",
    );
  }
  return false;
};

/** Antwort von GET risk-kpi/by-contacts — je Kontakt die Jahresreihe. */
type ContactKPIYears = { contactId: number; years: KPIYear[] };

/**
 * Deckel des Batch-Endpoints (`MAX_BATCH_CONTACTS` in `riskKPI.service.ts`).
 * Der Scope wählt nie mehr Firmen aus, als der Server beantwortet — sonst käme
 * eine Teilsumme über 50 Firmen als glatte Gesamtsumme über 60 zurück, ohne
 * jeden Marker.
 */
const MAX_VERBUND_FIRMEN = 50;

const KennzahlenTab: React.FC<Props> = ({ contactId, contactName }) => {
  const styles = useStyles();
  const rootRef = useRef<HTMLDivElement>(null);
  const topbarRef = useRef<HTMLDivElement>(null);
  useStickyStackOffset(rootRef, topbarRef);
  const today = new Date();
  const cy = today.getFullYear();
  const { hasPermission } = useAuth();

  /**
   * Schreibrecht fürs Organigramm-Popup. Das record-genaue `canWrite` des
   * Backends (FO/BO-Ownership) liegt hier nicht vor — der Reiter bekommt kein
   * Permission-Prop und lädt den Kontakt nicht selbst. Deshalb eine bewusste
   * Entscheidung statt eines stillen `true`: sonst ließe sich vom
   * Kennzahlen-Bildschirm aus die Konzernstruktur umbauen, ohne dass irgendwer
   * das Recht dazu geprüft hätte. Gegated wird mit dem globalen
   * Kontakt-Schreibrecht; die Ownership-Prüfung bleibt beim AuthzGuard, der
   * jede Relations-Änderung final abweist.
   */
  const canEditStruktur = hasPermission("contacts:update");
  const [orgChart, setOrgChart] = useState(false);

  const [years, setYears] = useState<number[]>([cy - 2, cy - 1, cy, cy + 1]);
  /** Rohdaten je Firma und Jahr — Quelle für Einzelansicht UND Aggregation. */
  const [perFirm, setPerFirm] = useState<Map<number, Map<number, Decoded>>>(new Map());
  const [yearData, setYearData] = useState<Map<number, Decoded>>(new Map());
  /** Zugeklappte Sektionen UND Untergruppen — eine gemeinsame Id-Menge (`COLLAPSIBLE_IDS`). */
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(COLLAPSIBLE_IDS), // start collapsed
  );
  const [loading, setLoading] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /**
   * Fehlgeschlagener Ladevorgang. MUSS sichtbar sein und die Bearbeitung
   * sperren: die Tabelle zeigt danach überall „—", was von „nichts erfasst"
   * nicht zu unterscheiden ist. Ein Speichern auf diesem Stand schickte für
   * ALLE angezeigten Jahre lauter `null` (siehe `encode`) und das Backend löscht
   * die Keys — der komplette Datensatz wäre weg, und `ContactKpi` führt keine
   * Historie.
   */
  const [loadError, setLoadError] = useState<string | null>(null);
  // Tabelle ist standardmäßig gesperrt (read-only). Erst aufschließen, dann
  // editieren; bei Änderung erscheint der Disketten-Speichern-Button.
  const [locked, setLocked] = useState(true);
  const [dirty, setDirty] = useState(false);
  /** Per „+ Branche" hinzugefügt — nur Anzeige, gespeichert wird erst ein Wert. */
  const [hinzugefuegt, setHinzugefuegt] = useState<number[]>([]);

  /**
   * Rückfrage vor jedem Vorgang, der den Bearbeitungsstand wegwirft: Scope-
   * wechsel, Jahresverschiebung, „Aktualisieren". Alle drei laden neu und bauen
   * `yearData` aus der Antwort neu auf — ohne die Rückfrage verschwände eine
   * gerade getippte Zahl kommentarlos.
   */
  const confirmDiscard = useCallback((): boolean | Promise<boolean> => {
    if (!dirty) return true;
    return bestaetigen({
      titel: "Änderungen verwerfen?",
      text: "Sie haben Kennzahlen geändert, die noch nicht gespeichert sind. Beim Neuladen gehen sie verloren.",
      ja: "Verwerfen",
      nein: "Weiter bearbeiten",
      ton: "danger",
    }).then((ok) => {
      if (!ok) return false;
      setDirty(false);
      setLocked(true);
      return true;
    });
  }, [dirty]);

  const scope = useVerbundScope(contactId, contactName, {
    onBeforeChange: confirmDiscard,
    maxSelected: MAX_VERBUND_FIRMEN,
  });
  const { scopeFirmen, selected, selectedIds, isConsolidated, singleName } = scope;

  /**
   * Im Verbund ist die Tabelle immer read-only (siehe Kommentar an `yearData`).
   *
   * Schon WÄHREND der Verbund lädt: trifft die Firmenliste ein, wählt
   * `useVerbundScope` automatisch alle Firmen aus — ohne Rückfrage, denn es ist
   * keine Nutzeraktion. Ein bis dahin getippter Wert wäre damit weg, weil
   * `yearData` im Verbund geleert wird. Und nach einem gescheiterten Ladevorgang
   * bleibt die Tabelle gesperrt, solange nicht bekannt ist, was wirklich
   * gespeichert ist.
   */
  const lockErzwungen =
    isConsolidated || loadError !== null || (scope.ebene === "verbund" && scope.networkLoading);
  const effectiveLocked = locked || lockErzwungen;

  // Fetch-Keys als String: `selectedIds` und `years` sind bei jedem Render neue
  // Array-Identitäten — als Dependency würden sie den Lade-Effect endlos neu
  // anstoßen.
  const selectedIdsKey = selectedIds.join(",");

  // Die Jahre wandern beim Pfeilklick sofort durch die Kopfzeile, der Fetch-Key
  // zieht verzögert nach: im Verbund lädt sonst jeder einzelne Klick sämtliche
  // Firmen komplett neu.
  const [yearsFetchKey, setYearsFetchKey] = useState(() => years.join(","));
  useEffect(() => {
    const key = years.join(",");
    if (key === yearsFetchKey) return;
    const t = setTimeout(() => setYearsFetchKey(key), 350);
    return () => clearTimeout(t);
  }, [years, yearsFetchKey]);

  // ---- Load
  // Sequenz-Guard über ALLE KPI-Fetches (Scope-/Jahreswechsel, „Aktualisieren",
  // Reload nach dem Speichern und nach dem Verwerfen): es zählt nur die Antwort
  // des jeweils letzten Requests. Ein `cancelled`-Flag im Effect reicht dafür
  // nicht — es kennt die Requests nicht, die außerhalb des Effects starten.
  const fetchSeqRef = useRef(0);

  const reload = useCallback(async () => {
    const ids = selectedIdsKey ? selectedIdsKey.split(",").map(Number) : [];
    const jahre = yearsFetchKey.split(",").map(Number);
    if (ids.length === 0) {
      setPerFirm(new Map());
      return;
    }
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const { data } = await apiFetch<ContactKPIYears[]>("risk-kpi/by-contacts", {
        params: { ids: ids.join(",") },
      });
      if (fetchSeqRef.current !== seq) return;
      const antwort = new Map((data ?? []).map((e) => [e.contactId, e.years ?? []]));
      const map = new Map<number, Map<number, Decoded>>();
      // Über `ids` iterieren, nicht über die Antwort: eine vom Backend per RBAC
      // herausgefilterte Firma bleibt so mit leeren Jahren im Verbund stehen,
      // statt aus Firmenliste und Aufteilung zu verschwinden.
      for (const id of ids) {
        const items = antwort.get(id) ?? [];
        const jahrMap = new Map<number, Decoded>();
        for (const y of jahre) {
          const found = items.find((i) => i.year === y);
          jahrMap.set(y, decode(found ?? { year: y }));
        }
        map.set(id, jahrMap);
      }
      setPerFirm(map);
      setLoadError(null);
      setDirty(false);
    } catch (err) {
      if (fetchSeqRef.current !== seq) return;
      console.error("KennzahlenTab: load failed", err);
      // BEWUSST keine fabrizierten leeren Jahre: eine Tabelle voller „—" ist von
      // „nichts erfasst" nicht zu unterscheiden. Der Fehler wird gemeldet und
      // die Bearbeitung gesperrt (siehe `loadError` / `effectiveLocked`) — ein
      // Speichern auf diesem Stand löschte sonst alle Jahre.
      setPerFirm(new Map());
      setDirty(false);
      setLoadError("Kennzahlen konnten nicht geladen werden — bitte „Aktualisieren“.");
    } finally {
      if (fetchSeqRef.current === seq) setLoading(false);
    }
  }, [selectedIdsKey, yearsFetchKey]);

  // `handleSave` und die Topbar-Buttons sind nicht memoisiert und schlössen über
  // das `reload` IHRES Renders — nach einem Kontaktwechsel während des Speicherns
  // liefe also ein Nachlade-Request mit den ALTEN Ids, gewänne den Sequenz-Guard
  // (er zieht die höchste Nummer) und schriebe die Zahlen des vorherigen Kontakts
  // in `perFirm`. Über die Ref läuft immer die aktuelle Fassung.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    void reload();
    return () => {
      // Wechsel/Unmount: alle laufenden Antworten entwerten — auch die aus
      // „Aktualisieren", die kein Effect gestartet hat.
      fetchSeqRef.current++;
    };
  }, [reload]);

  /**
   * Prop-Wechsel (andere Detailseite): sofort leeren und wieder sperren.
   *
   * WÄHREND des Renders, nicht im Effect: ein Effect läuft erst NACH dem Paint,
   * und in diesem einen Frame stünde das `yearData` des vorherigen Kontakts
   * unter dem Namen des neuen — bei entsperrter Tabelle sogar in editierbaren
   * Feldern. Das ist das dokumentierte React-Muster „Adjusting state when a prop
   * changes"; der Guard verhindert die Endlosschleife.
   */
  const [renderedContactId, setRenderedContactId] = useState(contactId);
  if (renderedContactId !== contactId) {
    // `collapsed` bleibt BEWUSST stehen: der Katalog ist statisch, die Zeilen-Ids
    // sind für jeden Kontakt dieselben. Ein Reset zwänge den Nutzer, nach jedem
    // Kontaktwechsel dieselben Gruppen erneut aufzuklappen — anders als die
    // Daten unten gehört der Klappzustand zur Ansicht, nicht zum Datensatz.
    setRenderedContactId(contactId);
    setPerFirm(new Map());
    setYearData(new Map());
    setLocked(true);
    setDirty(false);
    setSaveError(null);
    setLoadError(null);
    setHinzugefuegt([]);
  }

  /** Ankerfirma = die einzige gewählte Firma; im Verbund gibt es keine. */
  const anchorId = isConsolidated ? undefined : selectedIds[0];

  /**
   * `yearData` ist der Bearbeitungsstand GENAU EINER Firma und wird ausschließlich
   * aus `perFirm` gespeist — nie aus konsolidierten Werten.
   *
   * `handleSave` iteriert diese Map und PUTet sie auf EINE contactId: lägen dort
   * Verbund-Summen, schriebe ein Klick auf „Speichern" den Konzernumsatz als
   * Eigenumsatz der Ankerfirma. `ContactKpi` führt keine Historie — der
   * ursprüngliche Wert wäre unwiederbringlich weg. Im Verbund bleibt die Map
   * deshalb leer.
   */
  useEffect(() => {
    setYearData(anchorId ? new Map(perFirm.get(anchorId) ?? []) : new Map());
  }, [perFirm, anchorId]);

  // ---- Umsatzverteilung auf Branchen(-gruppen)
  //
  // Die Zeilen sind die Branchengruppen der Firma, dazu jede Gruppe mit einem
  // gespeicherten Wert (auch wenn sie nicht mehr zur Firma gehört — der Wert
  // bliebe sonst unsichtbar liegen) und die per „+ Branche" hinzugefügten.
  // Im Verbund bringt jede gewählte Firma ihre Gruppen mit.
  const { rows: brancheKatalog } = useBrancheGruppen();
  /** Branchengruppen-Ids je Firma aus der Akte (`/contact/:id/branchengruppen`). */
  const [firmenGruppen, setFirmenGruppen] = useState<Map<number, number[]>>(new Map());

  useEffect(() => {
    const ids = selectedIdsKey ? selectedIdsKey.split(",").map(Number) : [];
    const fehlend = ids.filter((id) => !firmenGruppen.has(id));
    if (fehlend.length === 0) return;
    let alive = true;
    void Promise.all(
      fehlend.map((id) =>
        branchengruppenLaden(id)
          .then((z) => [id, (z ?? []).map((e) => e.branchengruppeId)] as const)
          // Ohne Akte-Branchen geht es weiter: gespeicherte Werte stehen trotzdem da.
          .catch(() => [id, [] as number[]] as const),
      ),
    ).then((ergebnisse) => {
      if (!alive) return;
      setFirmenGruppen((prev) => {
        const next = new Map(prev);
        for (const [id, gruppen] of ergebnisse) next.set(id, gruppen);
        return next;
      });
    });
    return () => {
      alive = false;
    };
  }, [selectedIdsKey, firmenGruppen]);

  /** Firmen, deren Branchen-Zeilen gerade zählen: Ankerfirma oder alle gewählten. */
  const branchenFirmen = useMemo(
    () => (selectedIdsKey ? selectedIdsKey.split(",").map(Number) : []),
    [selectedIdsKey],
  );

  const branchenZeilenKey = useMemo(() => {
    const firma: number[] = [];
    const gespeichert: number[] = [];
    for (const id of branchenFirmen) {
      firma.push(...(firmenGruppen.get(id) ?? []));
      for (const d of perFirm.get(id)?.values() ?? []) {
        for (const e of revBranchenLesen(d, brancheKatalog)) gespeichert.push(e.branchengruppeId);
      }
    }
    const zeilen = brancheZeilenMergen({
      firmenGruppenIds: firma,
      gespeichertIds: gespeichert,
      hinzugefuegtIds: isConsolidated ? [] : hinzugefuegt,
      katalog: brancheKatalog,
    });
    return JSON.stringify(zeilen);
  }, [branchenFirmen, firmenGruppen, perFirm, brancheKatalog, hinzugefuegt, isConsolidated]);

  /**
   * Geladen und KEINE Branche in der Akte. Erst nach dem Laden — sonst blitzte
   * der Hinweis bei jeder Firma kurz auf.
   */
  const firmaOhneBranchen =
    branchenFirmen.length > 0 &&
    branchenFirmen.every((id) => firmenGruppen.has(id)) &&
    branchenFirmen.every((id) => (firmenGruppen.get(id) ?? []).length === 0);

  const {
    allRows,
    fieldRows,
    groupAncestors,
    groupFieldChildren,
    sectionAggRow,
  } = useMemo(
    () =>
      katalogAbleiten(
        brancheFeldZeilen(JSON.parse(branchenZeilenKey) as BrancheZeile[], brancheKatalog),
      ),
    [branchenZeilenKey, brancheKatalog],
  );

  /** Auswahl für „+ Branche": alle aktiven Gruppen, die noch keine Zeile haben. */
  const branchenOptionen = useMemo<SelectOption[]>(() => {
    const belegt = new Set(
      (JSON.parse(branchenZeilenKey) as BrancheZeile[]).map((z) => z.branchengruppeId),
    );
    return activeGruppen(brancheKatalog)
      .filter((g) => !belegt.has(g.id))
      .map((g) => ({ value: String(g.id), label: g.ebene2, hint: g.ebene1 }))
      .sort((a, b) => a.label.localeCompare(b.label, "de"));
  }, [branchenZeilenKey, brancheKatalog]);

  const brancheHinzufuegen = (v: string) => {
    const id = Number(v);
    if (!Number.isFinite(id) || id <= 0) return;
    setHinzugefuegt((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  // ---- Save all years currently shown
  const handleSave = async () => {
    // Bewusst als erste Zeilen: in der konsolidierten Ansicht gibt es nichts zu
    // speichern, und nach einem gescheiterten Ladevorgang wüsste `yearData`
    // nicht, was auf dem Server steht — ein PUT wäre in beiden Fällen
    // Datenverlust ohne Rückweg.
    if (isConsolidated) return;
    if (loadError) return;
    if (!anchorId) return;
    const eintraege = Array.from(yearData.entries());
    const ergebnisse = await Promise.allSettled(
      eintraege.map(([yr, d]) =>
        apiFetch(`risk-kpi/${anchorId}/${yr}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(encode(d)),
        }),
      ),
    );
    // `fetch` wirft bei 4xx/5xx nicht — vorher lief der Fehlerfall glatt durch den
    // try-Block und meldete „Gespeichert ✓", obwohl nichts geschrieben wurde
    // (z. B. 403 ohne Schreibrecht). `apiFetch` wirft; jede Antwort wird geprüft.
    const fehler = ergebnisse.filter((r) => r.status === "rejected");
    if (fehler.length > 0) {
      console.error("KennzahlenTab: save failed", fehler);
      const jahre = eintraege
        .filter((_, i) => ergebnisse[i].status === "rejected")
        .map(([yr]) => yr)
        .join(", ");
      // `dirty` bleibt STEHEN: sonst verschwindet der Speichern-Button und die
      // Eingaben existieren nur noch in diesem State — der nächste Reload wirft
      // sie weg, ohne dass jemand es gemerkt hätte.
      setSaveError(`Speichern fehlgeschlagen (${jahre}) — Änderungen sind nicht gesichert.`);
      return;
    }
    setSaveError(null);
    setSavedFlash(true);
    setDirty(false);
    setTimeout(() => setSavedFlash(false), 1800);
    // `perFirm` trägt weiterhin den Stand von vor dem Speichern — ohne Reload
    // zeigte ein Wechsel auf „Unternehmensverbund" die alten Zahlen der Ankerfirma in
    // der Summe. Über die Ref, weil zwischen Klick und Antwort der Kontakt
    // gewechselt haben kann (siehe `reloadRef`).
    void reloadRef.current();
  };

  // ---- Mutations
  const updateYear = (year: number, updater: (d: Decoded) => Decoded) => {
    setDirty(true);
    setYearData((prev) => {
      const next = new Map(prev);
      const cur = next.get(year) ?? decode({ year });
      next.set(year, updater(cur));
      return next;
    });
  };

  /** Sektion oder Untergruppe auf-/zuklappen — die Ids teilen sich eine Menge. */
  const toggleCollapse = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const collapseAll = () => setCollapsed(new Set(COLLAPSIBLE_IDS));
  // Leere Menge statt „alle Sektionen raus": „Alle" muss auch die Untergruppen
  // öffnen, sonst klappen zwar die Sektionen auf, die Länder- und Branchen-
  // Verteilung bleibt aber zu und der Knopf hielte sein Versprechen nicht.
  const expandAll = () => setCollapsed(new Set());

  // ---- Lock/Unlock: gesperrt = read-only; entsperren erlaubt Direkt-Edit.
  const toggleLock = async () => {
    // Im Verbund (und bei erzwungener Sperre) gibt es nichts aufzuschließen —
    // der Button ist disabled, hier steht der Riegel für den Fall, dass er über
    // die Tastatur ausgelöst wird.
    if (lockErzwungen) return;
    if (locked) {
      setLocked(false);
      return;
    }
    // entsperrt → sperren: ungespeicherte Änderungen ggf. verwerfen
    if (
      dirty &&
      !(await bestaetigen({
        titel: "Änderungen verwerfen und sperren?",
        text: "Die noch nicht gespeicherten Kennzahlen gehen dabei verloren.",
        ja: "Verwerfen und sperren",
        nein: "Weiter bearbeiten",
        ton: "danger",
      }))
    ) {
      return;
    }
    if (dirty) void reload();
    setLocked(true);
  };

  // ---- Year nav
  // Verschieben lädt nach (siehe `yearsFetchKey`) und baut `yearData` aus der
  // Antwort neu auf — ungespeicherte Eingaben wären ohne die Rückfrage weg,
  // auch in den drei überlappenden Jahren.
  const shiftYears = async (delta: number) => {
    if (!(await confirmDiscard())) return;
    setYears((prev) => prev.map((y) => y + delta));
  };

  const handleRefresh = async () => {
    if (!(await confirmDiscard())) return;
    void reloadRef.current();
  };

  // ---- Verbund-Aggregation: rein abgeleitet, landet NIE in `yearData`.
  const scopeIdsKey = scopeFirmen.map((f) => f.id).join(",");
  const colorMap = useMemo(
    () => buildColorMap(scopeIdsKey ? scopeIdsKey.split(",").map(Number) : []),
    [scopeIdsKey],
  );
  const colorOf = useCallback((id: number) => colorMap.get(id) ?? KZ_REST, [colorMap]);

  /**
   * Jahresdaten je Firma. Namen und Reihenfolge kommen aus `scopeFirmen`, NICHT
   * aus der Response — sonst wechselten Farben und Kürzel, sobald das Backend die
   * Reihenfolge ändert oder eine Firma weglässt.
   */
  const firmYearData = useMemo(() => {
    const map = new Map<number, FirmYearData[]>();
    const firmen = scopeFirmen.filter((f) => selected.has(f.id));
    for (const y of years) {
      map.set(
        y,
        firmen.map((f) => ({
          contactId: f.id,
          name: f.name,
          data: perFirm.get(f.id)?.get(y) ?? decode({ year: y }),
        })),
      );
    }
    return map;
  }, [years, scopeFirmen, selected, perFirm]);

  /**
   * Aggregierte Zelle je Feldzeile und Jahr. Auch die Sektionszeilen lesen hier
   * heraus (über `sectionAggRow`) — sie haben keine eigene Rechnung.
   */
  const aggByRow = useMemo(() => {
    const out = new Map<string, Map<number, AggCell>>();
    if (!isConsolidated) return out;
    for (const row of fieldRows) {
      const perYear = new Map<number, AggCell>();
      for (const y of years) perYear.set(y, aggregateCell(row, firmYearData.get(y) ?? []));
      out.set(row.id, perYear);
    }
    return out;
  }, [isConsolidated, years, firmYearData, fieldRows]);

  /**
   * Synthetisches `Decoded` je Jahr, damit die bestehenden `summary()`-Funktionen
   * der Sektionen im Verbund unverändert weiterlaufen und dabei die
   * konsolidierten Zahlen zeigen.
   */
  const consolidatedDecoded = useMemo(() => {
    const out = new Map<number, Decoded>();
    if (!isConsolidated) return out;
    for (const y of years) {
      out.set(y, buildConsolidatedDecoded(y, fieldRows, firmYearData.get(y) ?? []));
    }
    return out;
  }, [isConsolidated, years, firmYearData, fieldRows]);

  /** Wie viele der gewählten Firmen für dieses Jahr überhaupt etwas erfasst haben. */
  const firmenMitDaten = useMemo(() => {
    const out = new Map<number, number>();
    if (!isConsolidated) return out;
    for (const y of years) {
      const liste = firmYearData.get(y) ?? [];
      out.set(y, liste.filter((f) => fieldRows.some((r) => !isEmptyValue(r.get(f.data)))).length);
    }
    return out;
  }, [isConsolidated, years, firmYearData, fieldRows]);

  /** Datensatz hinter den Sektions-Summaries: konsolidiert oder Ankerfirma. */
  const decodedFor = (y: number): Decoded =>
    (isConsolidated ? consolidatedDecoded.get(y) : yearData.get(y)) ?? decode({ year: y });

  /**
   * Summary-Text einer Sektion ohne Leitzeile (`summaryAggRowId`).
   *
   * Konsolidiert enthält `decodedFor` nur die summierbaren Felder — eine Sektion
   * aus reinem Freitext (Liquidität, Verpflichtungen, Bürgschaften) fiele damit
   * auf „—" zurück und behauptete „nichts erfasst", obwohl die Firmen Werte
   * haben. Die stehen dann in den aufgeklappten Zeilen.
   */
  const sectionText = (sec: SectionRowDef, y: number): string => {
    const text = sec.summary(decodedFor(y));
    if (!isConsolidated || text !== "—") return text;
    const hatWerte = fieldRows.some(
      (r) => r.sectionId === sec.id && (aggByRow.get(r.id)?.get(y)?.parts.length ?? 0) > 0,
    );
    return hatWerte ? "siehe Details" : text;
  };

  /**
   * Trägt die Zeile in DIESEM Jahr einen Wert?
   *
   * Im Verbund MUSS die Antwort aus `aggByRow` kommen: `yearData` ist dort
   * absichtlich leer (siehe Kommentar dort) — sonst gälte im Verbund jede Zeile
   * als leer, der Zähler meldete überall „0 erfasst" und `hideWhenEmpty` risse
   * Zeilen weg, obwohl die Firmen Werte haben.
   */
  const rowHasValueInYear = useCallback(
    (r: FieldRowDef, y: number): boolean =>
      isConsolidated
        ? (aggByRow.get(r.id)?.get(y)?.parts.length ?? 0) > 0
        : !isEmptyValue(r.get(yearData.get(y) ?? decode({ year: y }))),
    [isConsolidated, aggByRow, yearData],
  );

  const visibleRows = useMemo(
    () =>
      filterVisibleRows(allRows, {
        collapsed,
        ancestors: groupAncestors,
        // Ein Wert in EINEM der sichtbaren Jahre hält die Zeile — sonst
        // verschwände sie, sobald das zuletzt angezeigte Jahr leer bleibt.
        hasValue: (r) => years.some((y) => rowHasValueInYear(r, y)),
      }),
    [allRows, groupAncestors, collapsed, years, rowHasValueInYear],
  );

  /**
   * Zählertexte der eingeklappten Gruppenzeile, einer je Jahresspalte —
   * inklusive der Enkel-Zeilen.
   */
  const groupCountsFor = (headerId: string): (string | null)[] =>
    groupCountLabels(headerId, groupFieldChildren, years, rowHasValueInYear);

  const titelSuffix = isConsolidated
    ? `konsolidiert (${selectedIds.length} Firmen)`
    : (singleName ?? contactName);

  /**
   * Zeile, hinter der „+ Branche" steht: die letzte sichtbare Branchen-Zeile,
   * ohne Branchen-Zeilen der Gruppenkopf selbst. Nur bei offener Gruppe und nur
   * in der Einzelfirmen-Ansicht — im Verbund wird nichts bearbeitet.
   */
  const branchenAnkerId = (() => {
    if (isConsolidated) return null;
    if (collapsed.has("umsatz") || collapsed.has(REV_BRANCHEN_HEADER_ID)) return null;
    let anker: string | null = null;
    for (const r of visibleRows) {
      if (r.id === REV_BRANCHEN_HEADER_ID) anker = r.id;
      else if (r.kind === "field" && r.groupId === REV_BRANCHEN_HEADER_ID) anker = r.id;
    }
    return anker;
  })();

  const mitBranchenAktion = (row: RowDef, el: React.ReactElement): React.ReactElement => {
    if (row.id !== branchenAnkerId) return el;
    return (
      <React.Fragment key={row.id}>
        {el}
        <tr className={styles.fieldRow}>
          <td
            colSpan={years.length + 1}
            className={mergeClasses(styles.fieldLabelCell, styles.fieldLabelIndent1)}
          >
            <div className={styles.brancheAktion}>
              {firmaOhneBranchen && (
                <span className={styles.brancheHinweis}>
                  Für diese Firma ist keine Branche hinterlegt — Branche in der Akte pflegen.
                </span>
              )}
              <div className={styles.brancheAuswahl}>
                <SearchableSelect
                  label="+ Branche"
                  name={`kennzahlen-branche-${contactId}`}
                  value=""
                  onChange={brancheHinzufuegen}
                  options={branchenOptionen}
                  allowFreeText={false}
                  placeholder="Branchengruppe suchen …"
                />
              </div>
            </div>
          </td>
        </tr>
      </React.Fragment>
    );
  };

  // ---- Render

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.topbar} ref={topbarRef}>
        <div className={styles.topbarLeft}>
          <span className={styles.title}>
            Kennzahlen{titelSuffix ? ` · ${titelSuffix}` : ""}
          </span>

          <div className={styles.yearControl}>
            {/* Der Glyph allein ist als zugänglicher Name nicht unterscheidbar —
                beide Knöpfe hießen „Jahre verschieben". */}
            <button
              className={mergeClasses(styles.ghostBtn, styles.yearStep)}
              onClick={() => shiftYears(-1)}
              title="Ein Jahr zurück"
              aria-label="Ein Jahr zurück"
            >
              ←
            </button>
            <span className={styles.yearRange}>
              {years[0]}–{years[years.length - 1]}
            </span>
            <button
              className={mergeClasses(styles.ghostBtn, styles.yearStep)}
              onClick={() => shiftYears(1)}
              title="Ein Jahr vor"
              aria-label="Ein Jahr vor"
            >
              →
            </button>
          </div>

          <button className={styles.ghostBtn} onClick={expandAll} title="Alle ausklappen">
            <ChevronDown20Regular /> Alle
          </button>
          <button className={styles.ghostBtn} onClick={collapseAll} title="Alle einklappen">
            <ChevronRight20Regular /> Keine
          </button>
        </div>

        <div className={styles.topbarRight}>
          {savedFlash && <span className={styles.toast}>Gespeichert ✓</span>}
          {loadError && (
            <span title={loadError} className={styles.errorBadge} role="alert">
              {loadError}
            </span>
          )}
          {saveError && (
            <span title={saveError} className={styles.errorBadge} role="alert">
              {saveError}
            </span>
          )}
          <button
            className={styles.ghostBtn}
            onClick={() => setOrgChart(true)}
            title="Organigramm des Unternehmensverbunds anzeigen"
          >
            <Organization20Regular /> Organigramm
          </button>
          {/* Rechner mit eigenem Auslöser, damit er wie die übrigen
              Topbar-Buttons aussieht statt wie sein Standard-Trigger. */}
          <CalculatorPopover
            trigger={({ open, toggle, ref }) => (
              <button
                ref={ref}
                type="button"
                className={mergeClasses(styles.ghostBtn, open && styles.ghostBtnOn)}
                onClick={toggle}
                title="Rechner öffnen"
              >
                <Calculator20Regular /> Rechner
              </button>
            )}
          />
          <button
            className={mergeClasses(styles.ghostBtn, lockErzwungen && styles.ghostBtnOff)}
            onClick={toggleLock}
            disabled={lockErzwungen}
            title={
              isConsolidated
                ? "Bearbeiten nur in der Einzelfirmen-Ansicht"
                : loadError
                  ? "Erst neu laden — der aktuelle Stand ist unbekannt"
                  : lockErzwungen
                    ? "Unternehmensverbund wird geladen …"
                    : locked
                      ? "Zum Bearbeiten aufschließen"
                      : "Bearbeitung sperren"
            }
          >
            {effectiveLocked ? (
              <>
                <LockClosed20Regular /> Gesperrt
              </>
            ) : (
              <>
                <LockOpen20Regular /> Entsperrt
              </>
            )}
          </button>
          <button className={styles.ghostBtn} onClick={handleRefresh} title="Neu laden">
            <ArrowSync20Regular /> Aktualisieren
          </button>
          {!effectiveLocked && dirty && (
            <button
              className={styles.primaryBtn}
              onClick={handleSave}
              title="Änderungen speichern"
            >
              <Save20Regular /> Speichern
            </button>
          )}
        </div>
      </div>

      {/* Scope-Leiste als eigene Zeile zwischen Topbar und Legende. */}
      <div className={styles.scopeWrap}>
        <VerbundScopeBar
          scope={scope}
          hint={
            isConsolidated
              ? `Werte aus ${selectedIds.length} Firmen addiert — Bearbeiten nur in der Einzelfirmen-Ansicht.`
              : undefined
          }
        />
      </div>

      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <Globe16Regular /> Jahre aufsteigend
        </span>
        <span>· Kategorien und Untergruppen klickbar zum Aus-/Einklappen</span>
        <span>· „—" = noch nicht erfasst</span>
        {loading && (
          <span className={styles.legendLoading}>
            <ArrowSync16Regular /> Lade…
          </span>
        )}
        {isConsolidated && (
          // Pflicht-Kennzeichnung: die Verbund-Spalte ist eine reine BRUTTO-Addition.
          // Ohne diesen Hinweis liest sie sich wie ein Konzernabschluss.
          <span className={styles.hinweis}>
            {/* Gold trägt hier das Icon (Grafik: 3:1 genügt), der Text steht in
                `ink` — siehe Kommentar an `hinweis`. */}
            <Warning16Regular style={{ flex: "0 0 auto", color: REFINED.goldInk }} />
            <span>
              Bruttosumme über {selectedIds.length} Firmen — <strong>keine Konsolidierung</strong>:
              Innenumsätze werden nicht eliminiert (das Feld „Innenumsätze?" ist Freitext, kein
              verrechenbarer Betrag), Beteiligungen gehen mit 100 % ein statt mit ihrer Quote.
              Die Kopfzeile zeigt je Jahr, wie viele Firmen überhaupt Daten haben — eine Teilsumme
              ist keine Gesamtsumme.
            </span>
          </span>
        )}
      </div>

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <colgroup>
            <col style={{ width: LABEL_COL_W }} />
            {years.map((y) => (
              <col key={y} style={{ width: YEAR_COL_W }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className={styles.thLabel}>Kennzahl</th>
              {years.map((y, i) => (
                <th
                  key={y}
                  className={mergeClasses(
                    styles.th,
                    // Das laufende Jahr ist der Bezugspunkt jeder Ablesung —
                    // ohne Hervorhebung muss man in vier gleich aussehenden
                    // Spalten erst suchen, wo „heute" steht.
                    y === cy && styles.thCurrent,
                    i > 0 && styles.colDivider,
                  )}
                >
                  {y}
                  {isConsolidated && (
                    // Abdeckung je Jahr: ohne sie liest sich eine Summe aus 3 von
                    // 5 Firmen wie die Gesamtsumme des Verbunds.
                    <div className={styles.thCoverage}>
                      {firmenMitDaten.get(y) ?? 0} von {selectedIds.length} Firmen
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => mitBranchenAktion(row, (() => {
              if (row.kind === "section") {
                const Icon = row.Icon;
                const isCollapsed = collapsed.has(row.id);
                // Sektionen mit Leitzeile bekommen im Verbund deren Zelle: Summe
                // + Ring (der Fall „Umsatz 2 Mio mit Ring"). Alle anderen
                // behalten ihren Summary-Text — jetzt aus den konsolidierten
                // Werten.
                const secAggRow = isConsolidated ? sectionAggRow.get(row.id) : undefined;
                const secAgg = secAggRow ? aggByRow.get(secAggRow.id) : undefined;
                const akzent = accentOf(row.id);
                return (
                  <tr
                    key={row.id}
                    className={styles.sectionRow}
                    onClick={() => toggleCollapse(row.id)}
                  >
                    <td
                      className={styles.sectionLabelCell}
                      // Akzent als inset-Schatten, nicht als border-left: eine
                      // Border verschöbe den Text der Sektionszeile gegenüber
                      // den Feldzeilen darunter.
                      style={{ boxShadow: `inset 3px 0 0 ${akzent}` }}
                    >
                      {/* `stopPropagation`, weil die Zeile denselben Toggle
                          trägt: ohne das feuerte ein Klick auf den Button beide
                          Handler und die Sektion klappte auf und sofort wieder
                          zu. */}
                      <button
                        type="button"
                        className={styles.rowToggleBtn}
                        aria-expanded={!isCollapsed}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCollapse(row.id);
                        }}
                      >
                        {isCollapsed ? <ChevronRight20Regular /> : <ChevronDown20Regular />}
                        {/* Fluent-Icons zeichnen mit `fill="currentColor"` — die
                            Akzentfarbe geht deshalb über `color`. */}
                        <Icon style={{ color: akzent }} />
                        <span>{row.label}</span>
                      </button>
                    </td>
                    {years.map((y, i) => {
                      const cell = secAgg?.get(y);
                      const text = cell ? null : sectionText(row, y);
                      return (
                        <td
                          key={y}
                          className={mergeClasses(
                            styles.sectionValueCell,
                            i > 0 && styles.colDivider,
                          )}
                        >
                          {cell ? (
                            <ConsolidatedAmountCell
                              cell={cell}
                              colorOf={colorOf}
                              label={row.label}
                              year={y}
                              // Format der Leitzeile, nicht fest „euro": käme
                              // eine Sektion mit einer Stückzahl als Leitzahl
                              // dazu, stünde dort sonst ein „€" hinter einer
                              // Kopfzahl.
                              type={secAggRow?.type ?? "euro"}
                            />
                          ) : (
                            <span
                              className={mergeClasses(
                                styles.sectionValueText,
                                // Sektionen ohne Ring müssen dessen Platz
                                // trotzdem freihalten, sonst steht die Summe um
                                // 28px weiter rechts als die Werte darunter.
                                isConsolidated && styles.sectionValueTextRing,
                                text === "—" && styles.emptyValue,
                              )}
                            >
                              {text}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              }

              const indentClass =
                row.indent === 1
                  ? styles.fieldLabelIndent1
                  : row.indent === 2
                    ? styles.fieldLabelIndent2
                    : "";

              // header: Gruppenkopf ohne eigenen Wert. Die Wert-Zellen bleiben
              // leer, damit das Spaltenraster intakt bleibt — eingeklappt trägt
              // jede Jahresspalte den Zähler IHRES Jahres.
              if (row.kind === "header") {
                const isCollapsed = collapsed.has(row.id);
                const counts = isCollapsed ? groupCountsFor(row.id) : [];
                return (
                  <tr
                    key={row.id}
                    className={mergeClasses(styles.fieldRow, styles.groupRow)}
                    onClick={() => toggleCollapse(row.id)}
                  >
                    <th
                      scope="row"
                      className={mergeClasses(
                        styles.fieldLabelCell,
                        indentClass,
                        styles.fieldHeaderCell,
                      )}
                    >
                      {/* Gleiche Machart wie die Sektionszeile — Button in der
                          Zelle, `stopPropagation` gegen den doppelten Toggle. */}
                      <button
                        type="button"
                        className={styles.rowToggleBtn}
                        aria-expanded={!isCollapsed}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCollapse(row.id);
                        }}
                      >
                        {isCollapsed ? <ChevronRight20Regular /> : <ChevronDown20Regular />}
                        <span>{row.label}</span>
                      </button>
                    </th>
                    {years.map((y, i) => (
                      <td
                        key={y}
                        className={mergeClasses(styles.fieldValueCell, i > 0 && styles.colDivider)}
                      >
                        {counts[i] && (
                          <span className={styles.groupCount}>{counts[i]}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              }

              // field
              return (
                <tr key={row.id} className={styles.fieldRow}>
                  <th scope="row" className={mergeClasses(styles.fieldLabelCell, indentClass)}>
                    {row.label}
                  </th>
                  {years.map((y, i) => {
                    // Zellinhalt wird GETAUSCHT, nicht überlagert: ein klickbarer
                    // Ring in derselben Zelle wie ein Editor konkurriert um die
                    // Klickfläche und zerschösse die Tab-Reihenfolge.
                    if (isConsolidated) {
                      const cell = aggByRow.get(row.id)?.get(y);
                      return (
                        <td
                          key={y}
                          className={mergeClasses(
                            styles.fieldValueCell,
                            i > 0 && styles.colDivider,
                          )}
                        >
                          {cell && (
                            <ConsolidatedAmountCell
                              cell={cell}
                              colorOf={colorOf}
                              label={row.label}
                              year={y}
                              type={row.type}
                            />
                          )}
                        </td>
                      );
                    }
                    const d = yearData.get(y) ?? decode({ year: y });
                    return (
                      <td
                        key={y}
                        className={mergeClasses(styles.fieldValueCell, i > 0 && styles.colDivider)}
                      >
                        <FieldEditor
                          styles={styles}
                          type={row.type}
                          value={row.get(d)}
                          locked={effectiveLocked}
                          // Die Eingaben tragen sonst nur einen Platzhalter („€",
                          // „%") als zugänglichen Namen — `<th scope="row">`
                          // hilft dem Screenreader in der Tabelle, ein
                          // fokussiertes `<input>` bekommt daraus keinen Namen.
                          ariaLabel={`${row.label} ${y}`}
                          onChange={(v) =>
                            updateYear(y, (cur) => row.set(cur, v))
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })()))}
          </tbody>
        </table>
      </div>

      {/* Bringt sein Overlay (`position: fixed`, eigener `.kvp-rd`-Scope) selbst
          mit und funktioniert deshalb auch aus dem Legacy-Renderpfad heraus.
          Bewusst OHNE `onChanged`: die Firmenliste des Verbunds kommt aus
          `useCompanyNetwork`, das keinen Reload anbietet — eine im Organigramm
          geänderte Struktur greift beim nächsten Öffnen des Kontakts. */}
      {orgChart && (
        <OrganigrammPopup
          companyId={contactId}
          companyName={singleName ?? contactName}
          canWrite={canEditStruktur}
          onClose={() => setOrgChart(false)}
        />
      )}
    </div>
  );
};

// ---------- FieldEditor ---------------------------------------------------

// Read-only-Darstellung einer Zelle (gesperrter Zustand).
const formatReadonly = (type: FieldType, value: any): string => {
  switch (type) {
    case "euro":
      return fmtEur(toEuroNumber(value));
    case "number":
      if (value == null || value === "") return "—";
      // Zahlen deutsch formatieren (2,5 statt 2.5); rohe Strings aus dem
      // Erfassen-Wizard bleiben, wie sie erfasst wurden.
      return typeof value === "number" ? fmtNum(value) : String(value);
    case "percent":
      return value != null && value !== "" ? `${value} %` : "—";
    case "text":
      return value != null && value !== "" ? String(value) : "—";
    case "yesNoText": {
      const v = (value as YesNoText) ?? {};
      if (v.active == null) return "—";
      return v.active ? (v.reason ? `ja — ${v.reason}` : "ja") : "nein";
    }
    case "yesNoNumText": {
      const v = (value as YesNoNumText) ?? {};
      if (v.active == null) return "—";
      if (!v.active) return "nein";
      const extra = [v.amount, v.reason].filter(Boolean).join(" · ");
      return extra ? `ja — ${extra}` : "ja";
    }
    case "yesNoPartner": {
      const v = (value as { active?: boolean | null; partner?: string; amount?: string }) ?? {};
      if (v.active == null) return "—";
      if (!v.active) return "nein";
      const extra = [v.partner, v.amount].filter(Boolean).join(" · ");
      return extra ? `ja — ${extra}` : "ja";
    }
    default:
      return "—";
  }
};

/**
 * Euro-Eingabe der Tabelle: gruppiert jetzt WÄHREND der Eingabe mit
 * Tausenderpunkten, statt den Rohwert zu zeigen und erst beim Blur zu formatieren.
 *
 * Anzeige, Bereinigung und Cursor-Arithmetik liegen komplett im
 * `GroupedNumberInput` (der frühere `focused`/`text`-State ist dort aufgegangen);
 * hier bleiben nur die Griffel-Klasse und der Platzhalter der Tabelle. Der
 * Vertrag nach außen ist unverändert — `onChange` feuert weiter erst beim Blur
 * und mit der geparsten Zahl, damit `updateYear` und das Dirty-Tracking des
 * Reiters gleich bleiben.
 */
const EuroCellInput: React.FC<{
  styles: ReturnType<typeof useStyles>;
  value: number | string | undefined;
  ariaLabel: string;
  onChange: (v: number | undefined) => void;
}> = ({ styles, value, ariaLabel, onChange }) => (
  <GroupedNumberInput
    className={styles.input}
    placeholder="€"
    value={value}
    ariaLabel={ariaLabel}
    onChange={onChange}
  />
);

const FieldEditor: React.FC<{
  styles: ReturnType<typeof useStyles>;
  type: FieldType;
  value: any;
  locked: boolean;
  /** Kennzahl + Jahr — zugänglicher Name der Eingaben (Platzhalter zählen nicht). */
  ariaLabel: string;
  onChange: (v: any) => void;
}> = ({ styles, type, value, locked, ariaLabel, onChange }) => {
  if (locked) {
    const align =
      type === "euro" || type === "number" || type === "percent" ? "right" : "left";
    const text = formatReadonly(type, value);
    return (
      <div
        className={mergeClasses(styles.readonlyValue, text === "—" && styles.emptyValue)}
        style={{ textAlign: align }}
      >
        {text}
      </div>
    );
  }
  if (type === "euro") {
    return (
      <EuroCellInput styles={styles} value={value} ariaLabel={ariaLabel} onChange={onChange} />
    );
  }
  /**
   * `number`, `percent` und `text` bleiben UNCONTROLLED (`defaultValue` + `key`)
   * und bekommen bewusst KEINE Live-Gruppierung.
   *
   * Das `key`-Remount ist die Resync-Strategie des Reiters nach einem externen
   * Reload; ein Umbau auf ein kontrolliertes Feld würde Dirty-Tracking und
   * Speicher-Semantik aller drei Typen mitverändern. Dafür gibt es keinen
   * Gegenwert: Prozentwerte liegen zwischen 0 und 100, Mitarbeiterzahlen selten
   * über 999 — Tausenderpunkte kommen dort praktisch nicht vor. Die Live-
   * Formatierung bleibt deshalb auf `euro` beschränkt.
   */
  if (type === "number") {
    return (
      <input
        className={styles.input}
        defaultValue={value != null ? String(value) : ""}
        placeholder="0"
        aria-label={ariaLabel}
        // Deutsches Zahlformat wie im Euro-Feld: der Punkt ist der TAUSENDER-
        // trenner. `parseNum` las ihn als Dezimalpunkt — ein aus dem
        // Erfassen-Wizard stammendes „1.200" wurde allein durch Anklicken und
        // Verlassen des Feldes zu 1,2.
        onBlur={(e) => onChange(parseEuroStrict(e.target.value) ?? undefined)}
        key={String(value ?? "")}
      />
    );
  }
  if (type === "percent") {
    return (
      <input
        className={styles.input}
        defaultValue={value ?? ""}
        placeholder="%"
        aria-label={ariaLabel}
        onBlur={(e) => onChange(e.target.value || undefined)}
        key={String(value ?? "")}
      />
    );
  }
  if (type === "text") {
    return (
      <input
        className={mergeClasses(styles.input, styles.textInput)}
        defaultValue={value ?? ""}
        placeholder="…"
        aria-label={ariaLabel}
        onBlur={(e) => onChange(e.target.value || undefined)}
        key={String(value ?? "")}
      />
    );
  }

  if (type === "yesNoText") {
    const v = (value as YesNoText) ?? {};
    return (
      <div className={styles.yesNoWrap}>
        <YesNoToggle
          styles={styles}
          ariaLabel={ariaLabel}
          value={v.active ?? null}
          onChange={(x) => onChange({ ...v, active: x })}
        />
        {v.active === true && (
          <input
            className={mergeClasses(styles.input, styles.textInput)}
            defaultValue={v.reason ?? ""}
            placeholder="warum…"
            aria-label={`${ariaLabel} — Begründung`}
            onBlur={(e) => onChange({ ...v, reason: e.target.value || undefined })}
            key={String(v.reason ?? "")}
          />
        )}
      </div>
    );
  }

  if (type === "yesNoNumText") {
    const v = (value as YesNoNumText) ?? {};
    return (
      <div className={styles.yesNoWrap}>
        <YesNoToggle
          styles={styles}
          ariaLabel={ariaLabel}
          value={v.active ?? null}
          onChange={(x) => onChange({ ...v, active: x })}
        />
        {v.active === true && (
          <>
            <input
              className={styles.input}
              defaultValue={v.amount ?? ""}
              placeholder="wieviel?"
              aria-label={`${ariaLabel} — Betrag`}
              onBlur={(e) => onChange({ ...v, amount: e.target.value || undefined })}
              key={"a-" + String(v.amount ?? "")}
            />
            <input
              className={mergeClasses(styles.input, styles.textInput)}
              defaultValue={v.reason ?? ""}
              placeholder="warum…"
              aria-label={`${ariaLabel} — Begründung`}
              onBlur={(e) => onChange({ ...v, reason: e.target.value || undefined })}
              key={"r-" + String(v.reason ?? "")}
            />
          </>
        )}
      </div>
    );
  }

  if (type === "yesNoPartner") {
    const v = (value as { active?: boolean | null; partner?: string; amount?: string }) ?? {};
    return (
      <div className={styles.yesNoWrap}>
        <YesNoToggle
          styles={styles}
          ariaLabel={ariaLabel}
          value={v.active ?? null}
          onChange={(x) => onChange({ ...v, active: x })}
        />
        {v.active === true && (
          <>
            <input
              className={mergeClasses(styles.input, styles.textInput)}
              defaultValue={v.partner ?? ""}
              placeholder="mit wem?"
              aria-label={`${ariaLabel} — Partner`}
              onBlur={(e) => onChange({ ...v, partner: e.target.value || undefined })}
              key={"p-" + String(v.partner ?? "")}
            />
            <input
              className={styles.input}
              defaultValue={v.amount ?? ""}
              placeholder="wie hoch?"
              aria-label={`${ariaLabel} — Höhe`}
              onBlur={(e) => onChange({ ...v, amount: e.target.value || undefined })}
              key={"a-" + String(v.amount ?? "")}
            />
          </>
        )}
      </div>
    );
  }

  return null;
};

const YesNoToggle: React.FC<{
  styles: ReturnType<typeof useStyles>;
  ariaLabel: string;
  value: boolean | null;
  onChange: (v: boolean | null) => void;
}> = ({ styles, ariaLabel, value, onChange }) => {
  return (
    <div className={styles.yesNoToggleRow}>
      {/* `mergeClasses` statt String-Verkettung: Griffel-Klassen sind atomar —
          aneinandergehängt entscheidet die Reihenfolge im Stylesheet, welche
          Hintergrundfarbe gewinnt, und der aktive Zustand kann verlieren. */}
      <button
        className={mergeClasses(styles.yesNoBtn, value === true && styles.yesNoBtnActiveYes)}
        aria-label={`${ariaLabel} — ja`}
        aria-pressed={value === true}
        onClick={(e) => {
          e.stopPropagation();
          onChange(value === true ? null : true);
        }}
      >
        ja
      </button>
      <button
        className={mergeClasses(styles.yesNoBtn, value === false && styles.yesNoBtnActiveNo)}
        aria-label={`${ariaLabel} — nein`}
        aria-pressed={value === false}
        onClick={(e) => {
          e.stopPropagation();
          onChange(value === false ? null : false);
        }}
      >
        nein
      </button>
    </div>
  );
};

export default KennzahlenTab;
